import { ILanguages, IListingPhotos, IObjectsHasTags, IVideos } from '@llws/lift-entity-interfaces';
import { IApiCommandResponse } from '@llws/api-common';
import { IQueryCommandOptions } from '@llws/dynamic-router-utils';
import { IApiResponse, IEditImage, IModifyPayload } from '@/shared';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import uploads from '@/shared/services/uploads';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { INewTranslationSettingsStoreConfig } from '@/shared/interfaces/new-translation-settings';
import { getByModelAndObjectId } from '@/shared/services/tags';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IFilterPayloadV2 } from '@/shared/interfaces/filter-payload-v2';

export interface IListingVideosWithExtraData extends IVideos {
  languages: ILanguages[];
  objectsHasTags: IObjectsHasTags[];
}

export interface ICustomListingVideoState extends IStateStrict<IVideos> {
  singleWithExtraData: IListingVideosWithExtraData | null;
}

// Base store configuration
const adminGatewayResourcePath: string = '/property-api/properties/:param/videos';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  limit: 999,
  orderBy: [{ field: 'orderBy', direction: 'asc' }],
};
const defaultGetSingleFilters: IQueryCommandOptions = {};

const translationSettings: INewTranslationSettingsStoreConfig = {
  model: 'videos',
  keys: ['title', 'description'],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IListingPhotos>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
  translationSettings,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'listing-videos/'

  state: {
    ...base.state,
    singleWithExtraData: null,
  },

  getters: {
    ...base.getters,
    singleWithExtraData: (state: ICustomListingVideoState) => state.singleWithExtraData,
  },

  mutations: {
    ...base.mutations,
    setSingleWithExtraData: (state: ICustomListingVideoState, payload: IListingVideosWithExtraData) => {
      state.singleWithExtraData = payload;
    },
    resetSingleWithExtraData: (state: ICustomListingVideoState) => {
      state.singleWithExtraData = null;
    },
  },

  actions: {
    ...base.actions,

    getWithTagsAndTranslations: async (
      ctx: IAction<IStateStrict<IVideos>>,
      payload: IFilterPayloadV2<number>,
    ) => {
      await ctx.dispatch('get', {
        ...payload,
        ...{ additional: { includeTranslations: true } },
      } as IFilterPayloadV2<number>);

      const tagsResponse = await getByModelAndObjectId('videos', ctx.getters.single.id);
      ctx.commit('setSingleWithExtraData', { ...ctx.getters.single, ...{ objectsHasTags: tagsResponse.data || [] } });
    },

    CreateNewVideo: async (
      ctx: IAction<IStateStrict<IVideos>>,
      payload: IModifyPayload<number, IVideos>,
    ) => {
      const videoResponse: IApiResponse<IVideos[], null> = await adminRequest.post(
        `/property-api/properties/${payload.parentId}/videos`,
        payload.data,
      );

      return videoResponse.data![0]!;
    },

    UpdateMany: async (
      ctx: IAction<IStateStrict<IVideos>>,
      payload: IModifyPayload<number, IVideos[]>,
    ) => {
      const videoResponse: IApiResponse<IVideos[], null> = await adminRequest.put(
        `/property-api/properties/${payload.parentId}/videos`,
        payload.data,
      );

      return videoResponse.data;
    },

    DeleteVideo: async (
      ctx: IAction<IStateStrict<IVideos>>,
      payload: IModifyPayload<number, IVideos>,
    ) => {
      const videoResponse: IApiCommandResponse<IVideos[]> = await adminRequest.delete(
        `/property-api/properties/${payload.parentId}/videos`,
        [payload.data.id || -1],
      );

      if (!videoResponse) return false;

      return true;
    },

    saveUploadedThumbnailImageEdits: async (
      ctx: IAction<IStateStrict<IVideos>>,
      payload: IModifyPayload<number, IVideos, { clientKey: string, filename: string, imageEdits: IEditImage[] }>,
    ) => {
      const uploadEditResult = await uploads.updateImageAsNew(
        payload.additional!.clientKey,
        payload.additional!.filename,
        'gallery',
        payload.additional!.imageEdits,
      );

      payload.data.thumbnail = uploadEditResult.filename.split('/').pop();

      await ctx.dispatch('save', {
        id: payload.id!,
        data: {
          thumbnail: payload.data.thumbnail,
        },
      } as IFilterPayloadV2<number>);
    },
  },
};
